/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { importSync } from '@embroider/macros';
import { ComponentLike } from '@glint/template';
import CampaignModel from 'cio/models/campaign';
import { CONDITION_TYPES, isEmptyCondition } from 'cio/utils/conditions';
import { AddStyleVariant, DropdownOption } from 'cio/components/condition-list';
import { ConditionType } from 'cio/utils/condition-types';
import { SerializedLegacyFilterType } from 'cio/types/conditions/legacy-filter';

interface EditArgs {
  variation?: string;
  subLayout?: string;
  allowAllVariationsSelection?: boolean;
  allowMessageVariationSelection?: boolean;
  allowMessageWildcardSelection?: boolean;
  allowInParentWildcardSelection?: boolean;
  excludedNewsletterConditionId?: string;
  condition: any;
  index?: number;
  isDisabled?: boolean;
  isCancelDisabled?: boolean;
  segments: Record<string, any>[];
  uuid?: string;
  direction?: string;
  gapSize?: string;
  canCreateSegment?: boolean;
  isNew?: boolean;
  dropdownInitiallyOpened?: any[];
  size?: string;
  campaign: CampaignModel;
  allowBlankName?: boolean;
  controlClassNames?: string;
  applyOnChange?: boolean;
  allowMessageConditionSpecificLink?: boolean;
  allowComparisonOperator?: boolean;
  allowArrayOperators?: boolean;
  allowedConditionOperators?: string[];
  allowJsonOperators?: boolean;
  autosuggestValueOverrides?: Record<string, string[] | { label: string; value: string }[]>;
  isLoadingAutosuggestValueOverrides?: boolean;
  showEventNameInEditor?: boolean;
  allowForeignEvent?: boolean;
  disableForeignEvent?: boolean;
  hiddenInverseOptions?: string[];
  event?: string;
  webhook?: string;
  objectTypeId?: string | number;
  addStyle?: AddStyleVariant;
  dropdownClass?: string;
  changeTypeOptions?: DropdownOption[];
  allowChangeTypeInline?: boolean;

  onApply: (condition: any, index?: any) => unknown;
  onCancel: () => unknown;
  onDelete: () => unknown;
  toggleCreatingSegment: () => unknown;
  onChangeType?: (type: SerializedLegacyFilterType) => unknown;
}

export default class Edit extends Component<EditArgs> {
  @tracked uuid = this.args.uuid ?? window.UUID.generate();

  AddStyleVariants = AddStyleVariant;

  get size() {
    return this.args.size ?? 'md';
  }

  get allowBlankName() {
    return this.args.allowBlankName ?? true;
  }

  get showEventNameInEditor() {
    return this.args.showEventNameInEditor ?? true;
  }

  get shouldDropdownInitiallyOpen() {
    return (this.args.dropdownInitiallyOpened ?? []).includes(this.conditionType);
  }

  get classNames() {
    let classNames = ['condition-list-item-editor'];

    if (this.args.size != null) {
      classNames.push(`condition-list-item-editor--${this.size}`);
    }

    if (this.args.variation === 'compact') {
      classNames.push('condition-list-item-editor--compact');
    }

    if (this.args.condition.type === 'attribute') {
      classNames.push('is-attribute');
    }

    return classNames.join(' ');
  }

  get editClassNames() {
    let classNames: string[] = ['fly-flex', 'condition-item__item'];

    if (this.args.direction === 'column' || this.args.variation === 'compact') {
      classNames.push('direction-column', 'items-stretch');
    } else {
      classNames.push('items-start');
    }

    return classNames.join(' ');
  }

  get icon() {
    let conditionType: keyof typeof CONDITION_TYPES = this.args.condition.type;
    return CONDITION_TYPES[conditionType].icon;
  }

  get conditionType() {
    if (this.args.condition.type === ConditionType.RelationshipAttribute) {
      return 'object-attribute';
    }

    return this.args.condition.type === 'collection_attribute' ? 'attribute' : this.args.condition.type;
  }

  get showCancelButton() {
    if (this.args.addStyle === this.AddStyleVariants.boxes || this.args.allowChangeTypeInline === true) {
      if (this.args.isNew === true) {
        return (this.args.index ?? -1) > 0;
      }
    }
    return true;
  }

  get showTypeChangeDropdown() {
    return this.args.onChangeType != null && (this.args.changeTypeOptions?.length ?? 0) > 1;
  }

  get conditionTypeComponent() {
    // components use `-` in the name, but actions use `_`. Swap 'em
    let module = importSync(`./${this.conditionType.replace('_', '-')}`) as {
      default: ComponentLike<any>;
    };

    return module.default;
  }

  onDelete() {
    return this.args.onDelete();
  }

  @action
  apply(condition: any) {
    if (this.args.applyOnChange ?? false) {
      return this.args.onApply(condition, this.args.index);
    }

    if (!isEmptyCondition(condition)) {
      return this.args.onApply(condition, this.args.index);
    } else {
      return this.onDelete();
    }
  }

  @action
  cancel() {
    return this.args.onCancel();
  }
}
